/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  forwardRef,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssDatePickerHeaderComponent } from '../date-picker-header/date-picker-header.component';
import { UmssTooltipDirective } from '../../directives/tooltip.directive';
import dayjs from 'dayjs';
import { horizontalSlide } from '../../animations';

export enum UmssCalendarSize {
  Small = 'Small',
  Regular = 'Regular',
  Large = 'Large'
}

export enum UmssCalendarVariant {
  Default = 'Default',
  Compact = 'Compact'
}

export enum UmssCalendarMarkerType {
  Dot = 'dot',
  Badge = 'badge',
  None = 'none'
}

export enum UmssCalendarView {
  Month = 'month',
  Week = 'week'
}

export type UmssCalendarLoadingScope = 'full' | 'navigation' | 'dates';

export interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isMarked: boolean;
  isDisabled: boolean;
  isWeekend: boolean;
}

@Component({
  selector: 'umss-calendar',
  standalone: true,
  imports: [CommonModule, UmssDatePickerHeaderComponent, UmssTooltipDirective],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  animations: [horizontalSlide],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssCalendarComponent),
      multi: true
    }
  ]
})
export class UmssCalendarComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @HostBinding('style.display') display = 'contents';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** Content props */
  @Input() currentDate: Date = new Date();
  @Input() markedDates: Date[] = []; // Dates to show with indicators (e.g., availability)
  @Input() markedDatesColored: { date: Date; color: string }[] = []; // Dates with individual dot colors
  @Input() selectedDate?: Date; // Currently selected/highlighted date
  @Input() locale: string = 'en-US';

  /** Variant props */
  @Input() size: UmssCalendarSize = UmssCalendarSize.Regular;
  @Input() variant: UmssCalendarVariant = UmssCalendarVariant.Default;
  @Input() view: UmssCalendarView = UmssCalendarView.Month; // Month or Week view
  @Input() showHeader: boolean = true;
  @Input() markerType: `${UmssCalendarMarkerType}` = UmssCalendarMarkerType.Dot; // Type of indicator for marked dates
  @Input() markerColor: string = 'var(--color-status-error)'; // Color for markers
  @Input() fullWidth: boolean = false; // When true, calendar uses width: 100% instead of max-width: 380px

  /** Multi-select props */
  @Input() multiSelect: boolean = false;
  @Input() selectedDates: Date[] = [];

  /** State props */
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() loadingScope: UmssCalendarLoadingScope = 'full';
  @Input() disabledDates: Date[] = [];
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() disabledDateTooltip: string = '';
  @Input() hideOutsideDays: boolean = false;
  @Input() allowDisabledDateClick: boolean = false;
  /** Disabled dates that should still be clickable (e.g., unavailable days that have booked shifts) */
  @Input() clickableDisabledDates: Date[] = [];

  /** Event outputs */
  @Output() monthChange = new EventEmitter<Date>();
  @Output() dateClick = new EventEmitter<Date>();
  @Output() dateHover = new EventEmitter<Date | null>();

  /** Internal state */
  currentMonth: Date = new Date();
  calendarDays: CalendarDay[] = [];
  weekdayNames: string[] = [];
  showLoading: boolean = false;
  private _loadingHideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Animation state — incremented on next, decremented on previous */
  calendarAnimIndex = signal<number>(0);
  calendarMonthKey = signal<string>('');
  calendarMonthEntries = computed(() => [this.calendarMonthKey()]);

  /** ControlValueAccessor state */
  private _value: Date | null = null;
  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** ControlValueAccessor: Write a new value */
  writeValue(value: Date | string | null): void {
    if (value === null || value === undefined) {
      this._value = null;
      this.selectedDate = undefined;
    } else if (value instanceof Date) {
      this._value = value;
      this.selectedDate = value;
      this.currentMonth = dayjs(value).toDate();
    } else if (typeof value === 'string') {
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        this._value = parsed.toDate();
        this.selectedDate = parsed.toDate();
        this.currentMonth = parsed.toDate();
      }
    }
    this.generateCalendar();
  }

  /** ControlValueAccessor: Register onChange callback */
  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  /** ControlValueAccessor: Register onTouched callback */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** ControlValueAccessor: Set disabled state */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    this.currentMonth = dayjs(this.currentDate).toDate();
    this.calendarMonthKey.set(this.toMonthKey(this.currentMonth));
    this.generateWeekdayNames();
    this.generateCalendar();
    this.syncLoadingState();
  }

  ngOnDestroy(): void {
    if (this._loadingHideTimer) clearTimeout(this._loadingHideTimer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const needsRegeneration =
      changes['currentDate'] ||
      changes['markedDates'] ||
      changes['markedDatesColored'] ||
      changes['selectedDate'] ||
      changes['selectedDates'] ||
      changes['multiSelect'] ||
      changes['view'] ||
      changes['disabledDates'] ||
      changes['minDate'] ||
      changes['maxDate'];

    if (needsRegeneration) {
      if (changes['currentDate'])
        this.currentMonth = dayjs(this.currentDate).toDate();

      if (changes['selectedDate']) this._value = this.selectedDate || null;

      // When data inputs change (not on first load), show skeleton briefly
      // to prevent stale dates from flashing
      const dataInputChanged =
        (changes['markedDates'] && !changes['markedDates'].firstChange) ||
        (changes['disabledDates'] && !changes['disabledDates'].firstChange) ||
        (changes['minDate'] && !changes['minDate'].firstChange) ||
        (changes['maxDate'] && !changes['maxDate'].firstChange);

      if (dataInputChanged) this.showLoading = true;

      this.generateCalendar();

      // If skeleton was shown due to data change (not external loading), schedule hide
      if (dataInputChanged && !this.loading) this.scheduleLoadingHide();
    }

    if (changes['locale']) this.generateWeekdayNames();

    if (changes['loading']) this.syncLoadingState();
  }

  /** Sync showLoading with loading input, adding a delay when hiding */
  private syncLoadingState(): void {
    if (this._loadingHideTimer) {
      clearTimeout(this._loadingHideTimer);
      this._loadingHideTimer = null;
    }

    if (this.loading) {
      this.showLoading = true;
      this.cdr.markForCheck();
    } else this.scheduleLoadingHide();
  }

  /** Schedule skeleton hide with a 300ms delay to prevent flash */
  private scheduleLoadingHide(): void {
    if (this._loadingHideTimer) clearTimeout(this._loadingHideTimer);

    this._loadingHideTimer = setTimeout(() => {
      this.showLoading = false;
      this._loadingHideTimer = null;
      this.cdr.markForCheck();
    }, 150);
  }

  /** Computed properties */
  get currentMonthNumber(): number {
    return this.currentMonth.getMonth();
  }

  get currentYear(): number {
    return this.currentMonth.getFullYear();
  }

  get headerSize(): 'Small' | 'Regular' | 'Large' {
    return this.size;
  }

  /** Generate weekday names based on locale */
  private generateWeekdayNames(): void {
    // Start from the Sunday of the current week
    const baseDate = dayjs().startOf('week');

    this.weekdayNames = [];

    for (let i = 0; i < 7; i++) {
      const date = baseDate.add(i, 'day');
      const dayName = date.toDate().toLocaleDateString(this.locale, {
        weekday: 'short'
      });
      this.weekdayNames.push(dayName);
    }
  }

  /** Generate calendar grid */
  private generateCalendar(): void {
    if (this.view === UmssCalendarView.Week) this.generateWeekCalendar();
    else this.generateMonthCalendar();
  }

  /** Generate week calendar (7 days) */
  private generateWeekCalendar(): void {
    const startOfWeek = dayjs(this.currentMonth).startOf('week');
    this.calendarDays = [];

    for (let i = 0; i < 7; i++) {
      const date = startOfWeek.add(i, 'day').toDate();
      this.calendarDays.push(this.createCalendarDay(date, true));
    }
  }

  /** Generate month calendar (42 days - 6 weeks) */
  private generateMonthCalendar(): void {
    const currentMonthDay = dayjs(this.currentMonth);
    const firstDayOfMonth = currentMonthDay.startOf('month');
    const daysInMonth = currentMonthDay.daysInMonth();
    const firstDayWeekday = firstDayOfMonth.day();

    // Get previous month days
    const prevMonthDays = firstDayWeekday;

    // Get next month days to fill the grid (6 weeks * 7 days = 42 cells)
    const totalCells = 42;
    const currentMonthCells = daysInMonth;
    const nextMonthDays = totalCells - prevMonthDays - currentMonthCells;

    this.calendarDays = [];

    // Add previous month days
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const date = firstDayOfMonth.subtract(i + 1, 'day').toDate();
      this.calendarDays.push(this.createCalendarDay(date, false));
    }

    // Add current month days
    for (let day = 0; day < daysInMonth; day++) {
      const date = firstDayOfMonth.add(day, 'day').toDate();
      this.calendarDays.push(this.createCalendarDay(date, true));
    }

    // Add next month days
    for (let day = 1; day <= nextMonthDays; day++) {
      const date = firstDayOfMonth.add(daysInMonth + day - 1, 'day').toDate();
      this.calendarDays.push(this.createCalendarDay(date, false));
    }
  }

  /** Create calendar day object */
  private createCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const today = dayjs().startOf('day').toDate();
    const dayDate = dayjs(date).startOf('day').toDate();

    return {
      date: dayDate,
      dayNumber: date.getDate(),
      isCurrentMonth,
      isToday: this.isSameDay(dayDate, today),
      isMarked: this.isDateMarked(dayDate),
      isDisabled: this.isDateDisabled(dayDate),
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    };
  }

  /** Check if date is marked */
  private isDateMarked(date: Date): boolean {
    return (
      this.markedDates.some((markedDate) => this.isSameDay(date, markedDate)) ||
      this.markedDatesColored.some((entry) => this.isSameDay(date, entry.date))
    );
  }

  /** Check if date is disabled */
  private isDateDisabled(date: Date): boolean {
    if (this.disabled) return true;

    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;

    return this.disabledDates.some((disabledDate) =>
      this.isSameDay(date, disabledDate)
    );
  }

  /** Check if date is selected */
  isDateSelected(day: CalendarDay): boolean {
    if (this.multiSelect) {
      return this.selectedDates.some((d) => this.isSameDay(d, day.date));
    }
    // Check both _value (from form control) and selectedDate (from @Input)
    const dateToCheck = this._value || this.selectedDate;
    if (!dateToCheck) return false;
    return this.isSameDay(day.date, dateToCheck);
  }

  /** Compare two dates (ignoring time) */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  /** Event handlers */
  onPreviousMonth(): void {
    if (this.disabled) return;

    const current = dayjs(this.currentMonth);
    const newDate =
      this.view === UmssCalendarView.Week
        ? current.subtract(7, 'day').toDate()
        : current.subtract(1, 'month').toDate();

    this.currentMonth = newDate;
    this.calendarAnimIndex.update((i) => i - 1);
    this.calendarMonthKey.set(this.toMonthKey(newDate));
    this.generateCalendar();
    this.monthChange.emit(newDate);
  }

  onNextMonth(): void {
    if (this.disabled) return;

    const current = dayjs(this.currentMonth);
    const newDate =
      this.view === UmssCalendarView.Week
        ? current.add(7, 'day').toDate()
        : current.add(1, 'month').toDate();

    this.currentMonth = newDate;
    this.calendarAnimIndex.update((i) => i + 1);
    this.calendarMonthKey.set(this.toMonthKey(newDate));
    this.generateCalendar();
    this.monthChange.emit(newDate);
  }

  private toMonthKey(date: Date): string {
    return dayjs(date).format('YYYY-MM-DD');
  }

  isClickableDisabledDate(date: Date): boolean {
    return this.clickableDisabledDates.some((d) => this.isSameDay(d, date));
  }

  onDayClick(day: CalendarDay): void {
    if (
      this.disabled ||
      (day.isDisabled &&
        !this.allowDisabledDateClick &&
        !this.isClickableDisabledDate(day.date))
    )
      return;

    if (this.multiSelect) {
      // In multi-select mode, just emit the click — parent manages the selection array
      this.onTouched();
      this.dateClick.emit(day.date);
      return;
    }

    // Update internal value
    this._value = day.date;
    this.selectedDate = day.date;

    // Notify form control
    this.onChange(day.date);
    this.onTouched();

    // Emit event for non-form usage
    this.dateClick.emit(day.date);

    // Regenerate calendar to update selected state
    this.generateCalendar();
  }

  onDayHover(day: CalendarDay | null): void {
    if (this.disabled) return;
    this.dateHover.emit(day ? day.date : null);
  }

  /** Track by function for performance */
  trackByDate(index: number, day: CalendarDay): string {
    return day.date.toISOString();
  }

  /**
   * Returns an array of length 1–3 representing how many dot markers
   * a given date should display, based on duplicate occurrences in markedDates.
   */
  getDateMarkDots(date: Date): number[] {
    const count = Math.min(
      this.markedDates.filter((d) => this.isSameDay(d, date)).length,
      3
    );
    return Array.from({ length: count }, (_, i) => i);
  }

  /**
   * Returns an array of colors for dot markers on a given date,
   * combining markedDates (using markerColor) and markedDatesColored entries.
   */
  getColoredDots(date: Date): string[] {
    const fromMarkedDates = this.markedDates
      .filter((d) => this.isSameDay(d, date))
      .map(() => this.markerColor);
    const fromColoredDates = this.markedDatesColored
      .filter((entry) => this.isSameDay(entry.date, date))
      .map((entry) => entry.color);
    return [...fromMarkedDates, ...fromColoredDates].slice(0, 3);
  }
}
